import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { bezels, helpers, orbitCam } from '../scene/context.js';
import { freeBounds, panelCorners, panelDescs, rightPanelDesc } from '../scene/panels.js';
import { applyLighting } from '../scene/lighting.js';
import { S, clamp } from '../state/store.js';

const frameGeo = new THREE.BufferGeometry();
const frame = new THREE.LineSegments(frameGeo, new THREE.LineBasicMaterial({
  color:0xF2C300,
  polygonOffset:true, polygonOffsetFactor:-4, polygonOffsetUnits:-4
}));
const surfGeo = new THREE.PlaneGeometry(1,1);
export const surf = new THREE.Mesh(surfGeo, new THREE.MeshBasicMaterial({
  color:0x0d1216, transparent:true, opacity:.55, side:THREE.DoubleSide, depthWrite:false,
  polygonOffset:true, polygonOffsetFactor:4, polygonOffsetUnits:4
}));
/* The second panel's surface, when there is one. Its own mesh rather than a
   rebuilt single geometry, because it has to be positioned and oriented by the
   panel descriptor — and it is placed from the SAME descriptor its camera uses,
   so a disagreement between the two shows up here in Orbit rather than only in
   the delivered pixels. */
export const surf2 = new THREE.Mesh(new THREE.PlaneGeometry(1,1), surf.material);
surf2.visible = false;

const frustGeo = new THREE.BufferGeometry();
const frust = new THREE.LineSegments(frustGeo, new THREE.LineBasicMaterial({
  color:0x5BA9D6, transparent:true, opacity:.38,
  polygonOffset:true, polygonOffsetFactor:-4, polygonOffsetUnits:-4
}));
/* The viewer's eye, drawn as a camera rather than a dot. Built in local space
   with the front of the lens at z = 0 and the body running back along +Z, so
   when the helper is aimed at the screen the lens sits exactly on the nodal
   point where the frustum lines converge. */
export function cameraHelperPositions(){
  const P = [];
  const seg = (a,b)=>P.push(a[0],a[1],a[2], b[0],b[1],b[2]);
  const box = (w,h,d,cx,cy,cz)=>{
    const x=w/2, y=h/2, z0=cz-d/2, z1=cz+d/2;
    const v = [[cx-x,cy-y,z0],[cx+x,cy-y,z0],[cx+x,cy+y,z0],[cx-x,cy+y,z0],
               [cx-x,cy-y,z1],[cx+x,cy-y,z1],[cx+x,cy+y,z1],[cx-x,cy+y,z1]];
    [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]]
      .forEach(([a,b])=>seg(v[a],v[b]));
  };
  const ring = (r,z,n=18)=>{
    for(let i=0;i<n;i++){
      const a=i/n*Math.PI*2, b=(i+1)/n*Math.PI*2;
      seg([Math.cos(a)*r,Math.sin(a)*r,z],[Math.cos(b)*r,Math.sin(b)*r,z]);
    }
  };
  box(1.5, 1.05, 1.7, 0, 0, 1.5);      // body
  box(0.46, 0.30, 0.46, 0, 0.66, 1.2); // viewfinder nub on top
  ring(0.50, 0.02);                     // lens front, on the nodal point
  ring(0.40, 0.65);                     // lens rear, meeting the body
  for(let i=0;i<6;i++){                 // barrel
    const a = i/6*Math.PI*2;
    seg([Math.cos(a)*0.50, Math.sin(a)*0.50, 0.02],
        [Math.cos(a)*0.40, Math.sin(a)*0.40, 0.65]);
  }
  return P;
}
/* Same "native linewidth is ignored" problem as the selection outline below —
   see its own comment for the full explanation. eyeCamMat is exported so
   scene/renderer.js can keep its resolution uniform in sync per frame. */
const eyeCamGeo = new LineSegmentsGeometry();
eyeCamGeo.setPositions(cameraHelperPositions());
export const eyeCamMat = new LineMaterial({color:0x5BA9D6, linewidth:2});
export const eyeCam = new LineSegments2(eyeCamGeo, eyeCamMat);
// a solid pip marks the exact nodal point the frustum converges on
const eyeDot = new THREE.Mesh(
  new THREE.SphereGeometry(0.12,14,10),
  new THREE.MeshBasicMaterial({color:0x5BA9D6})
);
eyeCam.add(eyeDot);
/* Invisible and fatter than eyeDot on purpose — same reasoning as every
   gizmo handle's own picker (scene/gizmo.js): sized generously enough for
   a mouse, wider again for a trackpad, not the exact radius of what's
   actually drawn. This is what ui/interaction.js raycasts against to let
   a solved (photo-calibrated) viewpoint be dragged directly, the same way
   a manual-setup Camera object already can be. */
export const eyePicker = new THREE.Mesh(
  new THREE.SphereGeometry(0.32,10,8),
  new THREE.MeshBasicMaterial({visible:false})
);
eyeCam.add(eyePicker);
helpers.add(surf, surf2, frame, frust, eyeCam);

/* selection outline */
/* Selection outline.

   A world-axis-aligned box balloons as soon as you rotate something: turn a long
   thin object 45 degrees and the box becomes a big loose cube that no longer
   describes the object at all. This is a unit wireframe cube driven by the
   object's own matrix and its LOCAL bounds, so the outline hugs the geometry and
   keeps the same proportions whichever way the object is turned. */
const SEL_EDGES = [
  -.5,-.5,-.5,  .5,-.5,-.5,   .5,-.5,-.5,  .5,-.5, .5,
   .5,-.5, .5, -.5,-.5, .5,  -.5,-.5, .5, -.5,-.5,-.5,
  -.5, .5,-.5,  .5, .5,-.5,   .5, .5,-.5,  .5, .5, .5,
   .5, .5, .5, -.5, .5, .5,  -.5, .5, .5, -.5, .5,-.5,
  -.5,-.5,-.5, -.5, .5,-.5,   .5,-.5,-.5,  .5, .5,-.5,
   .5,-.5, .5,  .5, .5, .5,  -.5,-.5, .5, -.5, .5, .5
];
/* A plain THREE.LineSegments/LineBasicMaterial line is 1px everywhere that
   matters — WebGL's native line width is not honoured by ANGLE (so not on
   Windows Chrome/Edge, this app's primary target) or most other modern
   drivers, `linewidth` on that material or not. LineSegments2/LineMaterial
   (three's own "fat lines" addon, already available under
   three/examples/jsm) draws real screen-space-width geometry instead, so
   this reliably renders at a comfortably visible thickness regardless of
   GPU/driver. worldUnits stays false (the default) so that thickness is in
   screen pixels, constant regardless of distance — resolution has to be
   kept in sync with the renderer's actual size for that math to work; see
   scene/renderer.js's syncSelLineRes(), called right before every
   gizmoScene render pass. */
const selBoxGeo = new LineSegmentsGeometry();
selBoxGeo.setPositions(SEL_EDGES);
export const selBoxMat = new LineMaterial({ color: 0x0D99FF, linewidth: 2 });
export const selBox = new LineSegments2(selBoxGeo, selBoxMat);
selBox.matrixAutoUpdate = false;
selBox.visible = false;

/* Bounds of a node in its OWN space, so the outline can be placed by the node's
   matrix rather than recomputed as a world-aligned box every frame. */
const _lbBox = new THREE.Box3(), _lbTmp = new THREE.Box3();
const _lbInv = new THREE.Matrix4(), _lbRel = new THREE.Matrix4();
export const _lbC = new THREE.Vector3(), _lbS = new THREE.Vector3();
export const _lbT = new THREE.Matrix4(), _lbSc = new THREE.Matrix4();
export function localBounds(node){
  _lbBox.makeEmpty();
  node.updateMatrixWorld(true);
  _lbInv.copy(node.matrixWorld).invert();
  node.traverse(c=>{
    if(!c.isMesh || !c.geometry) return;
    /* A text glow reaches well past the letters. Including it would inflate the
       selection box and make Frame Selection back the camera off for empty
       space, so decoration is excluded and the box hugs the object itself. */
    if(c.userData.fx) return;
    if(!c.geometry.boundingBox) c.geometry.computeBoundingBox();
    if(!c.geometry.boundingBox) return;
    _lbTmp.copy(c.geometry.boundingBox);
    _lbRel.multiplyMatrices(_lbInv, c.matrixWorld);
    _lbTmp.applyMatrix4(_lbRel);
    _lbBox.union(_lbTmp);
  });
  return _lbBox;
}
// selBox's real parent is gizmoScene (scene/gizmo.js) — the same "always on
// top, through the wide-margin frustum" pass the gizmo itself renders
// through, so a handle or an outline behind other geometry stays visible/
// grabbable. It used to also get added to the main `scene` here, but an
// Object3D can only have one parent — gizmo.js's later add() silently won,
// making that call dead weight that only served to mislead about where
// selBox actually renders.

/* A camera object, if present, IS the viewer — you place it by hand instead of
   recovering it from a photo. Its optical axis stays perpendicular to the screen:
   that is not a simplification, it is what an off-axis projection does. The
   frustum is sheared to reach the screen corners while the camera itself stays
   parallel to the screen plane, so the camera must never swing to "look at" the
   centre or it would misrepresent the projection. Hence no rotation control. */
export function cameraObj(){
  return S.objects.find(ob=>ob.type==='camera' && ob.visible) || null;
}
export function eyeVec(){
  const c = cameraObj();
  if(c) return new THREE.Vector3(c.pos.x, c.pos.y, c.pos.z);
  const e = S.eye || new THREE.Vector3(0,0,10);
  return new THREE.Vector3(e.x+S.nudge.x, e.y+S.nudge.y, e.z+S.nudge.z);
}

/* The cheap, per-frame part of the helpers: the frustum lines and the eye
   marker, both of which follow the viewer. Split out of rebuildHelpers so that
   dragging the camera does not rebuild plane geometry and relight the scene on
   every pointer move. */
/* Every panel's corners, the single screen's four plus a corner screen's other
   four. One list, so the frustum lines and the outline below cannot end up
   describing different geometry — and so adding a panel is not a change here. */
function allPanelCorners(){
  const out = [];
  for(const d of panelDescs())
    for(const v of panelCorners(d)) out.push([v.x, v.y, v.z]);
  return out;
}

/* The frustum lines go to the WALL's outer corners, not to every panel's.
   Through a single screen or a corner those are the same four or eight lines
   this always drew. Through a twelve-screen wall they are not: a cone per tile
   is forty-eight lines converging on one point, which reads as a scribble
   rather than as a viewing cone. The seams are still drawn — they come from the
   per-panel outline above, which is where they belong. */
function frustumCorners(){
  const hw = S.screen.w/2, hh = S.screen.h/2;
  return S.screenArray
    ? [[-hw,hh,0],[hw,hh,0],[hw,-hh,0],[-hw,-hh,0]]
    : allPanelCorners();
}

export function updateEyeHelpers(){
  const W=S.screen.w, H=S.screen.h;
  const c = frustumCorners();
  const e = eyeVec();
  const gv=[];
  for(let i=0;i<c.length;i++) gv.push(e.x,e.y,e.z, ...c[i]);
  frustGeo.setAttribute('position', new THREE.Float32BufferAttribute(gv,3));
  frustGeo.computeBoundingSphere();

  eyeCam.visible = !cameraObj();   // a camera object draws its own body
  eyeCam.position.copy(e);
  const up = Math.abs(e.clone().normalize().y) > .999
    ? new THREE.Vector3(0,0,1) : new THREE.Vector3(0,1,0);
  eyeCam.quaternion.setFromRotationMatrix(
    new THREE.Matrix4().lookAt(e, new THREE.Vector3(0,0,0), up));
  /* A fixed world size (screen.w/h * 0.028) reads fine at whatever
     distance frameOrbit() happens to frame by default, but shrinks to an
     unclickable speck the moment the orbit camera pulls back or the eye
     sits far from the screen — exactly the failure mode a gizmo handle
     avoids by keeping a constant APPARENT size instead of a constant
     world size (see scene/gizmo.js's gizmoWorldSize — same technique,
     reimplemented locally here rather than imported, to avoid a
     helpers.js <-> gizmo.js import cycle). Clamped to stay within a
     sane range of the screen's own scale either way. */
  const fwd = new THREE.Vector3(0,0,-1).applyQuaternion(orbitCam.quaternion);
  const camDist = Math.max(1e-3, e.clone().sub(orbitCam.position).dot(fwd));
  const viewportH = 2*camDist*Math.tan(orbitCam.fov*Math.PI/360);
  const m = Math.max(W,H);
  eyeCam.scale.setScalar(clamp(viewportH*0.05, m*0.01, m*0.6));
}

/* THE MARGIN, DRAWN. Unlit and untone-mapped so it is actually black rather
   than whatever the studio lighting would make of a black surface — a bezel is
   not a material in the scene, it is the absence of screen.

   Two bars per seam rather than a quad per gap cell: (cols-1) verticals the
   full height of the wall and (rows-1) horizontals its full width. They overlap
   where they cross, which for opaque black is not worth a special case, and it
   keeps the count linear in the grid instead of quadratic.

   OVER EVERYTHING, by depthTest:false and a renderOrder past every other
   object. A bezel is hardware: it physically covers whatever is behind it, and
   nothing is ever really in front of it — an object appearing to break the
   screen plane is the illusion the whole app exists to build, and letting that
   illusion occlude the frame it is being viewed through gets the physics
   backwards. This also retires the polygonOffset that used to keep it off the
   recess border it is coplanar with: with depth testing off there is no z-fight
   left to lose.

   EXACTLY ON THE SCREEN PLANE, all the same, and that has nothing to do with
   depth. Standing the bars 3 mm proud was the obvious move and it was wrong: a
   bar nearer the eye projects WIDER than the gap it fills, and on the side
   facing the viewer that overhang lands inside the neighbouring screen —
   measured, it darkened the last column of an exported tile by 12%, one black
   line down every seam of the built wall. z=0 is what makes the projection
   exact, and it is load-bearing. */
const bezelMat = new THREE.MeshBasicMaterial({
  color: 0x000000, toneMapped: false,
  transparent: true, opacity: 1,
  depthTest: false, depthWrite: false
});

/* THE MARGIN AS A HOLE, for On-site.

   A bezel is the one part of the wall that is not a screen, so on a mockup the
   honest thing to show in it is the building — and On-site already has the
   building: the render canvas is warped over the photograph by CSS, so a
   transparent pixel here IS the photograph, exactly registered, at no cost and
   with nothing to sample or keep in step.

   It has to REPLACE what is under it rather than blend with it. The background
   is cleared opaque behind the screen rectangle, and objects may be drawn in
   front; ordinary alpha blending over either of those leaves them showing
   through. One/Zero on both colour and alpha writes the source unchanged, so
   the destination becomes exactly rgba(0,0,0,0) — a hole punched through
   everything already in the buffer, which is what the bezel physically is.

   Orbit and Flat have no photograph behind the canvas, so a hole there would
   show the page rather than the building. scene/renderer.js switches them back
   to the black bars per pass — see setBezelPass. */
const bezelHoleMat = new THREE.MeshBasicMaterial({
  color: 0x000000, toneMapped: false,
  transparent: true, opacity: 0,
  depthTest: false, depthWrite: false,
  blending: THREE.CustomBlending,
  blendEquation: THREE.AddEquation,
  blendSrc: THREE.OneFactor,      blendDst: THREE.ZeroFactor,
  blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.ZeroFactor
});

/* Which of the two the bars wear for the pass about to be drawn.
 *
 * Per PASS, not per rebuild, because Split draws an orbit pane and a site pane
 * from the same scene in the same frame — a choice made once at rebuild time
 * would be wrong for one of them.
 *
 * @param {boolean} siteLike whether the photograph is behind this pass
 */
export function setBezelPass(siteLike){
  const hole = siteLike && S.bezelPhoto === true;
  const mat = hole ? bezelHoleMat : bezelMat;
  for(const m of bezels.children) m.material = mat;
}
const BEZEL_Z = 0;
const BEZEL_ORDER = 10000;

/* Two rectangles' overlap, as a rectangle — or null when they do not
 * genuinely overlap. Touching exactly (zero-area intersection) counts as
 * not overlapping: that case already triangulates correctly, so only a
 * REAL intersection needs merging away. */
function rectOverlap(a, b){
  const x0 = Math.max(a.x0, b.x0), x1 = Math.min(a.x1, b.x1);
  const y0 = Math.max(a.y0, b.y0), y1 = Math.min(a.y1, b.y1);
  return (x1 > x0 && y1 > y0) ? { x0, y0, x1, y1 } : null;
}

/* Screens that genuinely OVERLAP — a layout nothing prevents someone from
 * typing, since two real panels occupying the same space is a mistake in
 * the installation, not something this app can rule out by construction —
 * merged into their bounding union before they ever become holes.
 *
 * THE SHAPE-WITH-HOLES TRIANGULATOR ASSUMES NON-OVERLAPPING HOLES. Fed two
 * that genuinely intersect, it does not merely glitch at the overlap: it can
 * blacken an entire screen that had nothing to do with the mistake, because
 * the ear-clipping bridge it builds between one hole and the outer contour
 * can cut straight through the other. Touching holes (adjacent, zero gap)
 * are unaffected by this and stay separate — merging those too would cost
 * accuracy for a case that was never broken.
 *
 * The merge is imprecise on purpose rather than an exact polygon union: two
 * overlapping rectangles' TRUE union is an L-shape in general, and this
 * bezel only ever needs an axis-aligned rectangle to punch as a hole. Their
 * bounding box is always at least as large as the true union, so the margin
 * near an overlap may show slightly less bezel than a perfect union would —
 * a bounded, cosmetic inaccuracy on a layout that is already physically
 * impossible, in exchange for a triangulation that can never come apart. */
/* The first overlapping pair in `rects`, as [i, j], or null. Split out of
 * mergeOverlaps so the restart-after-each-merge loop below reads as what it
 * is — find one, merge it, look again — without a labelled break to say so. */
function firstOverlap(rects){
  for(let i = 0; i < rects.length; i++)
    for(let j = i + 1; j < rects.length; j++)
      if(rectOverlap(rects[i], rects[j])) return [i, j];
  return null;
}
function mergeOverlaps(rects){
  const out = rects.map(r => ({ ...r }));
  let pair;
  while((pair = firstOverlap(out))){
    const [i, j] = pair, a = out[i], b = out[j];
    out[i] = { x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0),
               x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) };
    out.splice(j, 1);                 // the merged rect may now overlap another — loop again
  }
  return out;
}

/* THE FREE-MODE BEZEL: everything in the wall that is not a screen, not a
   frame drawn around each one. A per-screen frame was tried first and was
   wrong — it drew as a set of thin outline strokes, where a real wall's
   mounting structure is a solid field behind and between the panels, all the
   way to the wall's own edge.

   One mesh, not one per screen: the wall's own rectangle as the outer path,
   every screen's rectangle punched out as a hole in it — the same
   THREE.Shape-with-holes idiom buildFrameGeometry (scene/geometry.js) already
   uses for a frame object's solid-rectangle-with-an-opening. Built from
   panelDescs() rather than a.screens directly, so it can never show black
   somewhere a screen was not actually placed, or vice versa — except at a
   genuine overlap, where mergeOverlaps above trades a little accuracy for a
   triangulation that always holds together. */
function freeBezelGeometry(){
  /* NOT S.screen.w/h. That is kept in step by onScreenChange, a SEPARATE
     call most of the way up whatever triggered this rebuild — trusting it
     here means the outer boundary and panelDescs()'s holes are each
     computed from their own source, with no guarantee the two agree at the
     instant this runs. A hole extending even slightly past the outer
     contour is malformed input to the triangulator below, and it does not
     fail quietly: an earlier version of this bug blackened an entire
     unrelated screen because one hole reached outside the shape it was
     meant to be cut from. freeBounds is the SAME call panelDescs() itself
     makes to place every hole, so the two can never disagree. */
  const b = freeBounds(S.screenArray.screens);
  const W = b.w, H = b.h;
  const shape = new THREE.Shape();
  shape.moveTo(-W/2,-H/2); shape.lineTo(W/2,-H/2); shape.lineTo(W/2,H/2); shape.lineTo(-W/2,H/2);
  shape.closePath();
  const rects = panelDescs().map(d => ({
    x0: d.pos.x - d.w/2, y0: d.pos.y - d.h/2, x1: d.pos.x + d.w/2, y1: d.pos.y + d.h/2
  }));
  for(const r of mergeOverlaps(rects)){
    const hole = new THREE.Path();
    hole.moveTo(r.x0, r.y0); hole.lineTo(r.x1, r.y0); hole.lineTo(r.x1, r.y1); hole.lineTo(r.x0, r.y1);
    hole.closePath();
    shape.holes.push(hole);
  }
  const geo = new THREE.ShapeGeometry(shape);
  /* Matches buildFrameGeometry's own shape-with-holes construction
     (scene/geometry.js) — without a computed bounding sphere, frustum
     culling has nothing reliable to test against, which reads as the mesh
     flickering in and out as the camera orbits rather than as a clean,
     always-present field. */
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

function rebuildBezels(){
  for(const m of bezels.children) m.geometry.dispose();
  bezels.clear();
  const a = S.screenArray;
  /* Both preview-only (store.js) — the gap itself is still there, and the
     export cannot see a bezel either way. */
  bezels.visible = S.showBezels !== false;
  bezelMat.opacity = Math.max(0, Math.min(1, S.bezelOpacity ?? 1));
  /* The hole's own opacity is not the slider's: it is the alpha it WRITES, and
     it writes nothing. Turning the margin down means showing less of the black
     bar; there is no partial hole, because a bezel either is the building or
     is a bezel. */
  if(!a) return;
  /* A NEW MESH IS BORN CORRECT, rather than black-until-the-next-render-pass-
     fixes-it. setBezelPass (above) is still what a live Orbit/Site switch or
     a Split's dual pass runs through — this only decides what a mesh THIS
     rebuild creates starts out wearing, for the view already on screen right
     now. Every edit that touches the array (typing a position, resizing,
     adding a screen) calls this, and On-site's own render loop is dirty-flag-
     driven — nothing forces one more frame afterward on its own. Without this
     a rebuild mid-edit could leave the black bars showing over the photograph
     until something else happened to ask for a redraw, which read as "the
     setting stopped working" even though S.bezelPhoto never changed. Same
     truthy test setBezelPass uses for On-site — inlined rather than imported,
     since scene/cameras.js (isSiteLike's home) already imports FROM this
     file, and importing back would be circular. */
  const isSiteLike = S.view==='site' || (S.view==='split' && !!S.photo);
  const mat = (isSiteLike && S.bezelPhoto === true) ? bezelHoleMat : bezelMat;
  const bar = (w, h, x, y) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    m.position.set(x, y, BEZEL_Z);
    m.renderOrder = BEZEL_ORDER;
    bezels.add(m);
  };
  if(a.mode === 'free'){
    const m = new THREE.Mesh(freeBezelGeometry(), mat);
    m.position.z = BEZEL_Z;
    m.renderOrder = BEZEL_ORDER;
    bezels.add(m);
    return;
  }
  if(!(a.gap > 0)) return;
  const W = S.screen.w, H = S.screen.h;
  for(let c = 1; c < a.cols; c++)
    bar(a.gap, H, -W/2 + c*(a.tile.w + a.gap) - a.gap/2, 0);
  for(let r = 1; r < a.rows; r++)
    bar(W, a.gap, 0, H/2 - r*(a.tile.h + a.gap) + a.gap/2);
}

export function rebuildHelpers(){
  const W=S.screen.w, H=S.screen.h;
  const c = allPanelCorners();
  /* One closed loop per panel — four corners at a time, so a corner screen
     draws both rectangles and the shared edge is simply drawn twice, once by
     each. Seeing that edge coincide is the quickest confirmation the recovered
     geometry is right, which is most of why this is worth drawing at all. */
  const fv=[];
  for(let p=0; p<c.length; p+=4)
    for(let i=0;i<4;i++){ fv.push(...c[p+i], ...c[p+(i+1)%4]); }
  frameGeo.setAttribute('position', new THREE.Float32BufferAttribute(fv,3));
  frameGeo.computeBoundingSphere();

  surf.geometry.dispose();
  surf.geometry = new THREE.PlaneGeometry(W,H);
  /* The second panel gets its own translucent surface, placed by the same
     descriptor its camera uses — so if the two ever disagree, it is visible in
     Orbit rather than only in the deliverable. */
  const r = rightPanelDesc();
  surf2.visible = !!r;
  if(r){
    surf2.geometry.dispose();
    surf2.geometry = new THREE.PlaneGeometry(r.w, r.h);
    surf2.position.copy(r.pos);
    surf2.quaternion.copy(r.quat);
  }

  rebuildBezels();

  const e = eyeVec();
  const gv=[];
  const fc = frustumCorners();          // the wall's outline, not every seam
  for(let i=0;i<fc.length;i++) gv.push(e.x,e.y,e.z, ...fc[i]);
  frustGeo.setAttribute('position', new THREE.Float32BufferAttribute(gv,3));
  frustGeo.computeBoundingSphere();

  /* Matrix4.lookAt puts +Z away from the target, so the object's -Z ends up
     facing it — exactly the convention a camera wants. */
  updateEyeHelpers();

  applyLighting(false);   // geometry moved; the environment map is unchanged
}
